import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { discardAs2DlqTask, listAs2DlqTasks, requeueAs2DlqTask } from '../api/as2Dlq';
import { ApiClientError } from '../api/http';
import { ConfirmDialog, type ConfirmDialogState } from '../components/ConfirmDialog';
import { CopyButton } from '../components/CopyButton';
import { PageHeader } from '../components/FeatureStatus';
import { PollingIndicator } from '../components/PollingIndicator';
import { EmptyState, ErrorState, LoadingState } from '../components/RequestState';
import { StatusBadge } from '../components/StatusBadge';
import { useAsyncData } from '../hooks/useAsyncData';
import { usePermission } from '../hooks/usePermission';
import { groupDlqTasks, type DlqGroupBy } from '../lib/dlqGrouping';
import type { As2DlqListQuery, As2DlqTaskRecord, As2DlqTaskStatus, As2DlqTaskType } from '../types/api';

const TASK_TYPES: As2DlqTaskType[] = ['OUTBOUND_SEND', 'INBOUND_ASYNC_MDN_DELIVERY', 'INBOUND_CALLBACK'];
const TASK_STATUSES: As2DlqTaskStatus[] = ['PENDING', 'RUNNING', 'COMPLETED', 'DEAD_LETTERED', 'DISCARDED'];
const PAGE_SIZE = 20;
const AS2_DLQ_POLL_INTERVAL_MS = 15_000;

function defaultFilters(): As2DlqListQuery {
  return {
    page: 0,
    size: PAGE_SIZE,
    taskType: '',
    partnerCode: '',
    status: '',
    fromDate: '',
    toDate: '',
  };
}

// Pre-applies filters carried in the incoming URL (e.g. a cross-link from an AS2 outbound/inbound
// message detail page pointing at its matching DLQ task — see lib/crossLinks.as2DlqTasksLink),
// mirroring the pattern already used by the AS2 message list pages.
function initialFiltersFromSearch(search: string): As2DlqListQuery {
  const params = new URLSearchParams(search);
  return {
    ...defaultFilters(),
    taskType: (params.get('taskType') as As2DlqTaskType | null) ?? '',
    partnerCode: params.get('partnerCode') ?? '',
  };
}

// A task can be requeued/discarded unless it's already in a terminal state.
function isActionable(task: As2DlqTaskRecord): boolean {
  return task.status !== 'DISCARDED' && task.status !== 'COMPLETED';
}

export function As2DlqPage() {
  const { search } = useLocation();
  const [filters, setFilters] = useState<As2DlqListQuery>(() => initialFiltersFromSearch(search));
  const [confirm, setConfirm] = useState<ConfirmDialogState | null>(null);
  const [resetRetryCountByRow, setResetRetryCountByRow] = useState<Record<string, boolean>>({});
  const [actionError, setActionError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<DlqGroupBy>('none');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [pollingPaused, setPollingPaused] = useState(false);
  const canRequeue = usePermission('integration.dlq.requeue');
  const canDiscard = usePermission('integration.dlq.discard');

  const { data, isLoading, error, refresh } = useAsyncData(
    () => listAs2DlqTasks(filters),
    [JSON.stringify(filters)],
    {
      debounceMs: 250,
      pollIntervalMs: pollingPaused ? undefined : AS2_DLQ_POLL_INTERVAL_MS,
      isPollingSuspended: Boolean(confirm),
    },
  );

  const tasks = data?.data ?? [];
  const deadLetteredCount = tasks.filter((task) => task.status === 'DEAD_LETTERED').length;
  const pendingCount = tasks.filter((task) => task.status === 'PENDING' || task.status === 'RUNNING').length;
  const groups = groupDlqTasks(tasks, groupBy);
  const selectedTasks = tasks.filter((task) => selectedIds.has(task.id));
  const eligibleRequeueIds = selectedTasks.filter(isActionable).map((task) => task.id);
  const eligibleDiscardIds = selectedTasks.filter(isActionable).map((task) => task.id);

  function patchFilters(patch: Partial<As2DlqListQuery>) {
    setFilters((current) => ({ ...current, ...patch, page: 0 }));
    setSelectedIds(new Set());
  }

  function toggleSelected(id: string) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleSelectAll(ids: string[]) {
    setSelectedIds((current) => {
      const allSelected = ids.every((id) => current.has(id));
      const next = new Set(current);
      ids.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
      return next;
    });
  }

  async function runRequeue(id: string, reason: string) {
    setActionError(null);
    try {
      await requeueAs2DlqTask(id, { reason: reason.trim(), resetRetryCount: Boolean(resetRetryCountByRow[id]) });
      await refresh();
    } catch (caught) {
      setActionError(formatDlqError(caught));
      throw caught;
    }
  }

  async function runDiscard(id: string, reason: string) {
    setActionError(null);
    try {
      await discardAs2DlqTask(id, { reason: reason.trim() });
      await refresh();
    } catch (caught) {
      setActionError(formatDlqError(caught));
      throw caught;
    }
  }

  // Bulk actions run as a batch of independent requests (no bulk endpoint exists on the AS2
  // gateway). Unlike the single-task actions, a bulk action never re-throws from onConfirm —
  // with N independent outcomes there's no single pass/fail to hand back to the confirm
  // dialog, so the dialog always closes and any partial-failure summary is surfaced through
  // the page-level error banner instead.
  async function runBulkRequeue(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(
      ids.map((id) => requeueAs2DlqTask(id, { reason: reason.trim(), resetRetryCount: false })),
    );
    reportBulkOutcome('requeue', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  async function runBulkDiscard(ids: string[], reason: string) {
    setActionError(null);
    const results = await Promise.allSettled(ids.map((id) => discardAs2DlqTask(id, { reason: reason.trim() })));
    reportBulkOutcome('discard', ids.length, results);
    setSelectedIds(new Set());
    await refresh();
  }

  function reportBulkOutcome(action: 'requeue' | 'discard', total: number, results: PromiseSettledResult<As2DlqTaskRecord>[]) {
    const failures = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
    if (failures.length === 0) return;
    const firstReason = formatDlqError(failures[0].reason);
    setActionError(
      `${failures.length} of ${total} tasks failed to ${action}${failures.length === total ? '' : ` (${total - failures.length} succeeded)`}: ${firstReason}`,
    );
  }

  function buildRequeueConfirmState(id: string, referenceId: string, reason: string): ConfirmDialogState {
    return {
      title: 'Requeue AS2 task',
      message: `Requeue ${referenceId} for re-processing by the AS2 gateway.${resetRetryCountByRow[id] ? ' The retry count will be reset to zero.' : ''}`,
      confirmLabel: 'Requeue',
      risk: 'medium',
      detailLabel: 'Reason',
      detailValue: reason,
      detailPlaceholder: 'Why this task is being requeued',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildRequeueConfirmState(id, referenceId, value)),
      onConfirm: () => runRequeue(id, reason),
    };
  }

  function buildDiscardConfirmState(id: string, referenceId: string, reason: string): ConfirmDialogState {
    return {
      title: 'Discard AS2 task',
      message: `Permanently discard ${referenceId}. This cannot be requeued afterward.`,
      confirmLabel: 'Discard',
      risk: 'high',
      requireText: 'DISCARD',
      detailLabel: 'Reason',
      detailValue: reason,
      detailPlaceholder: 'Why this task is being discarded',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildDiscardConfirmState(id, referenceId, value)),
      onConfirm: () => runDiscard(id, reason),
    };
  }

  function buildBulkRequeueConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    return {
      title: 'Bulk requeue AS2 tasks',
      message: `Requeue ${count} selected task${count === 1 ? '' : 's'} for re-processing by the AS2 gateway. Each task is requeued independently — some may succeed while others fail.`,
      confirmLabel: `Requeue ${count} task${count === 1 ? '' : 's'}`,
      risk: 'medium',
      detailLabel: 'Reason',
      detailValue: reason,
      detailPlaceholder: 'Why these tasks are being requeued',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkRequeueConfirmState(ids, value)),
      onConfirm: () => runBulkRequeue(ids, reason),
    };
  }

  function buildBulkDiscardConfirmState(ids: string[], reason: string): ConfirmDialogState {
    const count = ids.length;
    // Friction scales with blast radius: a single discard requires typing "DISCARD"; a bulk
    // discard requires typing the exact count being discarded, so the operator has to
    // consciously register how many records they're about to permanently remove.
    const requireText = `DISCARD ${count}`;
    return {
      title: 'Bulk discard AS2 tasks',
      message: `Permanently discard ${count} selected task${count === 1 ? '' : 's'}. This cannot be requeued afterward. Type "${requireText}" to confirm.`,
      confirmLabel: `Discard ${count} task${count === 1 ? '' : 's'}`,
      risk: 'high',
      requireText,
      detailLabel: 'Reason',
      detailValue: reason,
      detailPlaceholder: 'Why these tasks are being discarded',
      detailRequired: true,
      onDetailChange: (value) => setConfirm(buildBulkDiscardConfirmState(ids, value)),
      onConfirm: () => runBulkDiscard(ids, reason),
    };
  }

  function renderTasksTable(rows: As2DlqTaskRecord[]) {
    const ids = rows.map((task) => task.id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    return (
      <table className="data-table data-table-cozy">
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                aria-label="Select all in this table"
                checked={allSelected}
                onChange={() => toggleSelectAll(ids)}
              />
            </th>
            <th>Task</th>
            <th>Type</th>
            <th>Partner</th>
            <th>Status</th>
            <th>Attempts</th>
            <th>Next retry</th>
            <th>Last error</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((task) => (
            <tr key={task.id} className={selectedIds.has(task.id) ? 'selected-row' : undefined}>
              <td>
                <input
                  type="checkbox"
                  aria-label={`Select task ${task.referenceId}`}
                  checked={selectedIds.has(task.id)}
                  onChange={() => toggleSelected(task.id)}
                />
              </td>
              <td>
                <div className="table-primary">
                  <span>{task.referenceId}</span>
                  <span className="inline-copy">
                    <span className="mono" title={task.id}>{shortId(task.id)}</span>
                    <CopyButton value={task.id} label="Copy task ID" />
                  </span>
                </div>
              </td>
              <td>{task.taskType}</td>
              <td>{task.partnerCode ?? 'n/a'}</td>
              <td><StatusBadge value={task.status} /></td>
              <td>{task.retryCount}/{task.maxAttempts}</td>
              <td>{task.nextRetryAt ? new Date(task.nextRetryAt).toLocaleString('en-GB') : 'n/a'}</td>
              <td>{task.lastError ?? task.deadLetterReason ?? 'n/a'}</td>
              <td className="actions-cell">
                {canRequeue && isActionable(task) ? (
                  <>
                    <label className="inline-check">
                      <input
                        type="checkbox"
                        checked={Boolean(resetRetryCountByRow[task.id])}
                        onChange={(event) => setResetRetryCountByRow((current) => ({ ...current, [task.id]: event.target.checked }))}
                      />
                      Reset count
                    </label>
                    <button type="button" onClick={() => setConfirm(buildRequeueConfirmState(task.id, task.referenceId, ''))}>
                      Requeue
                    </button>
                  </>
                ) : null}
                {canDiscard && isActionable(task) ? (
                  <button type="button" className="danger-link" onClick={() => setConfirm(buildDiscardConfirmState(task.id, task.referenceId, ''))}>
                    Discard
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  return (
    <div className="page-grid">
      <PageHeader
        eyebrow="AS2 Gateway"
        title="AS2 dead-letter queue"
        purpose="Inspect and operate on AS2 gateway delivery tasks that failed, are retrying, or have been dead-lettered."
        status="PRODUCTION_READY"
        connected
        next="Requeue after fixing the underlying cause; discard only when the task is confirmed obsolete."
      />
      <section className="panel">
        <div className="panel-header">
          <div>
            <h2>AS2 delivery tasks</h2>
            <p>Outbound sends, async MDN deliveries, and inbound callback retries tracked by the AS2 gateway.</p>
          </div>
          <div className="panel-actions">
            <PollingIndicator paused={pollingPaused} onToggle={() => setPollingPaused((paused) => !paused)} />
            <button type="button" onClick={() => void refresh()}>
              Refresh
            </button>
          </div>
        </div>
        <div className="operations-card-grid">
          <article className="operations-card">
            <span className="operations-card-label">Visible dead-lettered</span>
            <strong>{deadLetteredCount}</strong>
            <p>Tasks in the current result set that need operator review.</p>
          </article>
          <article className="operations-card">
            <span className="operations-card-label">Pending / running</span>
            <strong>{pendingCount}</strong>
            <p>Tasks still being processed or waiting on the next retry attempt.</p>
          </article>
        </div>
        {actionError ? <ErrorState message={actionError} /> : null}
        <div className="toolbar toolbar-grid">
          <select
            aria-label="Task type"
            value={filters.taskType ?? ''}
            onChange={(event) => patchFilters({ taskType: event.target.value as As2DlqTaskType | '' })}
          >
            <option value="">All task types</option>
            {TASK_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
          </select>
          <select
            aria-label="Task status"
            value={filters.status ?? ''}
            onChange={(event) => patchFilters({ status: event.target.value as As2DlqTaskStatus | '' })}
          >
            <option value="">All statuses</option>
            {TASK_STATUSES.map((status) => <option key={status} value={status}>{status}</option>)}
          </select>
          <input
            aria-label="Partner code"
            placeholder="Partner code"
            value={filters.partnerCode ?? ''}
            onChange={(event) => patchFilters({ partnerCode: event.target.value })}
          />
          <input
            aria-label="From date"
            type="date"
            value={filters.fromDate ?? ''}
            onChange={(event) => patchFilters({ fromDate: event.target.value })}
          />
          <input
            aria-label="To date"
            type="date"
            value={filters.toDate ?? ''}
            onChange={(event) => patchFilters({ toDate: event.target.value })}
          />
          <button type="button" className="ghost-button" onClick={() => { setFilters(defaultFilters()); setSelectedIds(new Set()); }}>
            Reset filters
          </button>
        </div>
        {isLoading ? <LoadingState label="Loading AS2 DLQ tasks..." /> : null}
        {error ? <ErrorState message={error} onRetry={refresh} /> : null}
        {!isLoading && !error && tasks.length === 0 ? <EmptyState label="No AS2 tasks matched the current filters." /> : null}
        {!isLoading && !error && tasks.length > 0 ? (
          <>
            <div className="table-controls" aria-label="DLQ grouping and bulk actions">
              <div className="segmented-control" aria-label="Group by">
                <button type="button" aria-pressed={groupBy === 'none'} className={groupBy === 'none' ? 'active' : ''} onClick={() => setGroupBy('none')}>No grouping</button>
                <button type="button" aria-pressed={groupBy === 'partner'} className={groupBy === 'partner' ? 'active' : ''} onClick={() => setGroupBy('partner')}>By partner</button>
                <button type="button" aria-pressed={groupBy === 'reason'} className={groupBy === 'reason' ? 'active' : ''} onClick={() => setGroupBy('reason')}>By failure reason</button>
              </div>
              <span>{selectedIds.size} of {tasks.length} visible selected</span>
              {(canRequeue || canDiscard) ? (
                <div className="panel-actions">
                  {canRequeue ? (
                    <button
                      type="button"
                      disabled={eligibleRequeueIds.length === 0}
                      onClick={() => setConfirm(buildBulkRequeueConfirmState(eligibleRequeueIds, ''))}
                    >
                      Bulk requeue ({eligibleRequeueIds.length})
                    </button>
                  ) : null}
                  {canDiscard ? (
                    <button
                      type="button"
                      className="danger-link"
                      disabled={eligibleDiscardIds.length === 0}
                      onClick={() => setConfirm(buildBulkDiscardConfirmState(eligibleDiscardIds, ''))}
                    >
                      Bulk discard ({eligibleDiscardIds.length})
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {groupBy === 'none' ? renderTasksTable(tasks) : (
              <div className="page-stack">
                {groups.map((group) => (
                  <details key={group.key} className="panel" open>
                    <summary>
                      <strong>{group.label}</strong>
                      {' — '}{group.tasks.length} task{group.tasks.length === 1 ? '' : 's'}
                    </summary>
                    {renderTasksTable(group.tasks)}
                  </details>
                ))}
              </div>
            )}
            <div className="pagination-bar">
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: Math.max(0, (current.page ?? 0) - 1) }))}
                disabled={(filters.page ?? 0) <= 0}
              >
                Previous
              </button>
              <span>Page {(filters.page ?? 0) + 1}</span>
              <button
                type="button"
                onClick={() => setFilters((current) => ({ ...current, page: (current.page ?? 0) + 1 }))}
                disabled={((data?.page ?? 0) + 1) * (data?.pageSize ?? PAGE_SIZE) >= (data?.total ?? 0)}
              >
                Next
              </button>
            </div>
          </>
        ) : null}
      </section>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function shortId(value: string): string {
  return value.length > 18 ? `${value.slice(0, 18)}...` : value;
}

function formatDlqError(caught: unknown): string {
  if (caught instanceof ApiClientError && caught.status === 403) {
    return 'Not authorized to perform this AS2 DLQ action. Ask an administrator for the required DLQ permission.';
  }
  return caught instanceof Error ? caught.message : 'Unexpected AS2 DLQ action failure.';
}
